import {styled} from '@linaria/react';
import React, {
  Children,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  type JSX,
} from 'react';
import {
  mediaDesktopAndUp,
  mediaLargeScreens,
  mediaMaxL,
  mediaMaxM,
  mediaMaxS,
  mediaMobileOnly,
  mediaTabletAndDown,
  mediaTabletAndUp,
} from '../../../constants/customMedia';
import {
  GRID_GUTTER_WIDTH,
  GRID_TOTAL_COLUMNS,
  gridColsWidth,
} from '../../../constants/gridSystem';
import {hideScrollBar, touchScroll} from '../../../constants/scrolling';
import {useExpensiveMedia} from '../../../hooks/useExpensiveMedia';
import {useIsomorphicLayoutEffect} from '../../../hooks/useIsomorphicLayoutEffect/useIsomorphicLayoutEffect';
import {useKeyPress} from '../../../hooks/useKeyPress/useKeyPress';
import {HorizontalSliderHeaderControls} from './HorizontalSliderHeaderControls';
import {SlideControls} from './SlideControls';
import {useAutoplay} from './hooks/useAutoplay';
import {useSlider} from './hooks/useSlider';
import {SliderControlProps} from './types';

export type HorizontalSliderProps = {
  dataTestId?: string;
  title?: ReactNode;
  disableHeaderControl?: boolean;
  className?: string;
  style?: React.CSSProperties;
  as?: keyof JSX.IntrinsicElements | React.ComponentType<any>;
  hideSideButtons?: boolean;
  constrained?: boolean;
  hasHeaderBottomGap?: boolean;
  currentOuterIndex?: number;
  disableGutter?: boolean;
  /**
   * Optional: This is the index of the slide that should be centered in the slider on initial render
   */
  currentCenteredItem?: {
    index?: number;
    scrollBehavior: 'smooth' | 'auto';
  };
  slideCount?: number;
  slideCountSmall?: number;
  slideCountMedium?: number;
  slideCountLarge?: number;
  slideCountExtraLarge?: number;
  headerContent?: ReactNode;
  SlideControlComponent?: React.ComponentType<SliderControlProps>;
  SlideControlComponentBottom?: React.ComponentType<SliderControlProps>;
  theme?: 'light' | 'dark';
  enableKeyboardNavigation?: boolean;
  scrollSingleElementOnNavigation?: boolean;
  /**
   * If provided, the scroll position will be stored in page state.
   * This will restore the scroll position on history back navigation.
   */
  uniqueKey?: string;
  /**
   * apply overscroll-behavior:contain rule
   * This rule is added to fix problems where overflow:hidden is breaking scroll behaviour for user.
   * On enable of this property user will only be able to scroll inside this component, nothing outside will be impacted
   */
  disableOverscroll?: boolean;
  moveSideNavTop?: boolean;
  onIndexChange?: (currentSlide: number) => void;
  onSliderElement?: (sliderElement: HTMLUListElement) => void;
  onMouseLeave?: VoidFunction;

  navButtonsZIndex?: number;
  addSlidesMarginToSliderWidth?: boolean;
  /**
   * If true, the slider will loop back to the first slide after reaching the last slide
   */
  loop?: boolean;
  scrollToSlideBehavior?: 'auto' | 'smooth';
  sliderTotalCount?: number;

  // If set allow the slider to auto-scroll after some seconds
  autoplayDuration?: number;
  enableGridLayout?: boolean;
};

const MOBILE_PADDING = 16;
const TABLET_PADDING = 24;
const GRID_DESKTOP_PADDING = 30;
const LARGE_PADDING = 64;

const basePaddingByViewport = {
  [mediaMobileOnly]: MOBILE_PADDING,
  [mediaTabletAndUp]: TABLET_PADDING,
  [mediaLargeScreens]: LARGE_PADDING,
} as const;

const gridPaddingByViewport = {
  [mediaMobileOnly]: MOBILE_PADDING,
  [mediaTabletAndUp]: TABLET_PADDING,
  [mediaDesktopAndUp]: GRID_DESKTOP_PADDING,
  [mediaLargeScreens]: LARGE_PADDING,
} as const;

/**
 * HorizontalSlider Component can be used for sliding elements. Example Recos on a Category Page
 * @design https://zpl.io/a3jvdy1
 * @design https://zpl.io/bLWdLY7
 */
// eslint-disable-next-line complexity
export const HorizontalSlider = ({
  dataTestId,
  children,
  title,
  className,
  style,
  as,
  disableHeaderControl,
  hideSideButtons = false,
  constrained = false,
  hasHeaderBottomGap = false,
  slideCount = 3,
  slideCountSmall = slideCount,
  slideCountMedium = slideCountSmall,
  slideCountLarge = slideCountMedium,
  slideCountExtraLarge = slideCountLarge,
  headerContent,
  SlideControlComponent,
  SlideControlComponentBottom,
  currentOuterIndex,
  currentCenteredItem,
  enableKeyboardNavigation,
  scrollSingleElementOnNavigation = false,
  theme = 'light',
  disableOverscroll,
  moveSideNavTop,
  onIndexChange,
  onSliderElement,
  onMouseLeave,
  navButtonsZIndex,
  addSlidesMarginToSliderWidth,
  loop = false,
  sliderTotalCount,
  disableGutter = false,
  scrollToSlideBehavior = 'auto',
  autoplayDuration,
  enableGridLayout,
  ref,
  ...restProps
}: React.PropsWithChildren<HorizontalSliderProps> & {
  ref?: React.Ref<HTMLElement>;
}) => {
  const innerRef = useRef<HTMLDivElement>(null);
  const sliderRef = (ref ??
    innerRef) as React.RefObject<HTMLUListElement | null>;

  const paddingByViewport = enableGridLayout
    ? gridPaddingByViewport
    : basePaddingByViewport;

  const padding = useExpensiveMedia(paddingByViewport, MOBILE_PADDING);

  const {
    next,
    prev,
    scrollToSlide,
    nextIsActive,
    prevIsActive,
    currentSlide,
    scrollToNextItem,
    scrollToPrevItem,
  } = useSlider(sliderRef, {
    padding: constrained ? 0 : padding,
    ...restProps,
  });

  useEffect(() => {
    if (sliderRef.current) {
      onSliderElement?.(sliderRef.current);
    }
  }, [sliderRef, onSliderElement]);

  useEffect(() => {
    if (
      typeof currentOuterIndex === 'number' &&
      currentSlide !== currentOuterIndex
    ) {
      scrollToSlide(currentOuterIndex, {behavior: scrollToSlideBehavior});
    }
  }, [currentOuterIndex]);

  useIsomorphicLayoutEffect(() => {
    if (
      currentCenteredItem?.index !== undefined &&
      currentSlide !== currentCenteredItem?.index
    ) {
      scrollToSlide(currentCenteredItem.index, {
        behavior: currentCenteredItem.scrollBehavior,
        centered: true,
      });
    }
  }, [
    currentCenteredItem?.index,
    currentCenteredItem?.scrollBehavior,
    scrollToSlide,
  ]);

  const isFirstRender = useRef(true);

  useIsomorphicLayoutEffect(() => {
    if (
      isFirstRender.current &&
      (currentCenteredItem === undefined ||
        currentCenteredItem?.index === currentSlide)
    ) {
      isFirstRender.current = false;
    } else if (!isFirstRender.current) {
      onIndexChange?.(currentSlide);
    }
  }, [currentSlide]);

  useKeyPress({
    key: 'ArrowRight',
    onKeyDown: () => {
      if (scrollSingleElementOnNavigation) {
        scrollToNextItem();
      } else {
        next();
      }
    },
    disabled: !enableKeyboardNavigation,
  });

  useKeyPress({
    key: 'ArrowLeft',
    onKeyDown: () => {
      if (scrollSingleElementOnNavigation) {
        scrollToPrevItem();
      } else {
        prev();
      }
    },
    disabled: !enableKeyboardNavigation,
  });

  const itemsPerSlideByViewport = useMemo(
    () => ({
      [mediaMaxL]: slideCountExtraLarge,
      [mediaMaxM]: slideCountLarge,
      [mediaMaxS]: slideCountMedium,
      [mediaTabletAndDown]: slideCountSmall,
      [mediaMobileOnly]: slideCount,
    }),
    [
      slideCountExtraLarge,
      slideCountLarge,
      slideCountMedium,
      slideCountSmall,
      slideCount,
    ],
  );
  const itemsPerSlide = useExpensiveMedia(
    itemsPerSlideByViewport,
    slideCountExtraLarge,
  );
  const childrenCount = sliderTotalCount ?? Children.count(children);
  const maxPages = Math.ceil(childrenCount / itemsPerSlide);

  const hasOverflow = maxPages > 1;
  const shouldDisplayHeaderControls = !disableHeaderControl && hasOverflow;
  const shouldDisplayTitle = Boolean(title);
  const shouldDisplayHeader = shouldDisplayTitle || shouldDisplayHeaderControls;

  const {inViewRef, autoplayStarted} = useAutoplay({
    duration: autoplayDuration,
    currentSlide,
    next,
    scrollToSlide,
    isNextActive: nextIsActive,
    loop,
  });

  return (
    <Wrapper
      data-testid={dataTestId || 'horizontalSlider'}
      className={className}
      style={style}
      as={as}
      ref={inViewRef}
      data-enable-grid-layout={enableGridLayout}
    >
      {shouldDisplayHeader && (
        <Header
          data-testid="header"
          constrained={constrained}
          $marginBottom={hasHeaderBottomGap}
        >
          <HeaderTitle>{title || <div />}</HeaderTitle>
          <div>
            {headerContent}
            {shouldDisplayHeaderControls && (
              <HorizontalSliderHeaderControls
                SlideControlComponent={SlideControlComponent}
                currentSlide={currentSlide}
                maxPages={maxPages}
                next={next}
                nextIsActive={nextIsActive}
                prev={prev}
                prevIsActive={prevIsActive}
                scrollToNextItem={scrollToNextItem}
                scrollToPrevItem={scrollToPrevItem}
                scrollToSlide={scrollToSlide}
              />
            )}
          </div>
        </Header>
      )}
      <SliderContainer data-testid="sliderContainer">
        <SliderWrapper
          data-testid="sliderWrapper"
          itemsPerSlide={itemsPerSlide}
          maxPages={maxPages}
          constrained={constrained}
          ref={sliderRef}
          onMouseLeave={onMouseLeave}
          $containOverscroll={disableOverscroll}
          $addSlidesMarginToSliderWidth={addSlidesMarginToSliderWidth}
          $disableGutter={disableGutter}
          tabIndex={0}
        >
          {children}
        </SliderWrapper>
        <SlideControls
          hasOverflow={hasOverflow}
          hideSideButtons={hideSideButtons}
          isPrevActive={prevIsActive}
          isNextActive={nextIsActive}
          theme={theme}
          moveSideNavTop={moveSideNavTop}
          navButtonsZIndex={navButtonsZIndex}
          loop={loop}
          onPrev={prev}
          onNext={next}
          scrollToSlide={scrollToSlide}
        />
      </SliderContainer>
      {SlideControlComponentBottom && (
        <SlideControlComponentBottom
          next={() => {
            if (!nextIsActive && loop) {
              scrollToSlide(0, {
                behavior: 'smooth',
              });
            } else {
              next();
            }
          }}
          prev={prev}
          scrollToSlide={scrollToSlide}
          nextIsActive={nextIsActive}
          loop={loop}
          prevIsActive={prevIsActive}
          maxPages={maxPages}
          currentSlide={currentSlide}
          scrollToNextItem={scrollToNextItem}
          scrollToPrevItem={scrollToPrevItem}
          navButtonsZIndex={navButtonsZIndex}
          autoplayDuration={autoplayStarted ? autoplayDuration : undefined}
        />
      )}
    </Wrapper>
  );
};

const Wrapper = styled.section`
  width: 100%;
`;

export const Header = styled.header<{
  constrained?: boolean;
  $marginBottom?: boolean;
}>`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;

  margin: ${props => {
    if (props.constrained) {
      return props.$marginBottom ? '24px' : '0px';
    }

    return `24px ${MOBILE_PADDING}px`;
  }};

  @media ${mediaTabletAndUp} {
    margin-left: ${props => (props.constrained ? '0' : `${TABLET_PADDING}px`)};
    margin-right: ${props => (props.constrained ? '0' : `${TABLET_PADDING}px`)};
  }

  @media ${mediaDesktopAndUp} {
    [data-enable-grid-layout='true'] & {
      margin-left: ${props =>
        props.constrained ? '0' : `${GRID_DESKTOP_PADDING}px`};
      margin-right: ${props =>
        props.constrained ? '0' : `${GRID_DESKTOP_PADDING}px`};
    }
  }

  @media ${mediaLargeScreens} {
    && {
      margin-left: ${props => (props.constrained ? '0' : `${LARGE_PADDING}px`)};
      margin-right: ${props =>
        props.constrained ? '0' : `${LARGE_PADDING}px`};
    }
  }
`;

export const HeaderTitle = styled.div`
  display: flex;
  white-space: pre;
  width: 100%;
`;

const SliderContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

export const SliderWrapper = styled.ul<{
  constrained?: boolean;
  itemsPerSlide: number;
  maxPages: number;
  $containOverscroll?: boolean;
  $addSlidesMarginToSliderWidth?: boolean;
  $disableGutter?: boolean;
}>`
  height: 100%;
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  overscroll-behavior: ${props =>
    props.$containOverscroll ? 'contain' : 'auto'};
  margin: 0;
  padding: 0;
  list-style: none;

  --spacing: 5px;

  [data-enable-grid-layout='true'] & {
    --spacing: ${GRID_GUTTER_WIDTH};
  }

  ${touchScroll};
  ${hideScrollBar};

  padding: ${props => (props.constrained ? '' : `0 ${MOBILE_PADDING}px`)};
  scroll-padding: ${props =>
    props.constrained ? '' : `0 ${MOBILE_PADDING}px`};

  @media ${mediaTabletAndUp} {
    padding: ${props => (props.constrained ? '' : `0 ${TABLET_PADDING}px`)};

    scroll-padding: ${props =>
      props.constrained ? '' : `0 ${TABLET_PADDING}px`};
  }

  @media ${mediaDesktopAndUp} {
    [data-enable-grid-layout='true'] & {
      padding: ${props =>
        props.constrained ? '' : `0 ${GRID_DESKTOP_PADDING}px`};
      scroll-padding: ${props =>
        props.constrained ? '' : `0 ${GRID_DESKTOP_PADDING}px`};
    }
  }

  @media ${mediaLargeScreens} {
    && {
      padding: ${props => (props.constrained ? '' : `0 ${LARGE_PADDING}px`)};
      scroll-padding: ${props =>
        props.constrained ? '' : `0 ${LARGE_PADDING}px`};
    }
  }

  width: ${props =>
    `calc(100% + ${
      props.$addSlidesMarginToSliderWidth && props.maxPages > 1
        ? 'var(--spacing)'
        : 0
    })`};

  & > * {
    flex-shrink: 0;
    scroll-snap-align: start;
    margin-right: ${props => (props.$disableGutter ? 0 : 'var(--spacing)')};
    width: 0;
    /*
      if $disableGutter is true, flex-basis should be 100%
      otherwise, it should be 100% / props.itemsPerSlide - 5px
    */
    flex-basis: ${props =>
      props.$disableGutter
        ? '100%'
        : `calc((100% / ${props.itemsPerSlide} - var(--spacing)))`};

    [data-enable-grid-layout='true'] & {
      flex-basis: ${props =>
        props.$disableGutter
          ? '100%'
          : gridColsWidth(
              Math.floor(GRID_TOTAL_COLUMNS / props.itemsPerSlide),
            )};
    }

    &:last-child {
      margin-right: 0;
    }
  }
`;
